import type { Collection, Table } from "dexie";
import type { MangoQuery, MangoSelector } from "./MangoTypes";
import { mangoCompile } from "./mangoCompile";
import { isProvablyEmpty } from "./isProvablyEmpty";
import { expandMangoSelector } from "./expandMangoQuery";
import { normalizeSelector, generateTemplateKey, isPlaceholder } from "./templateNormalize";
import { compileTemplateSelector, type ParameterizedPredicate } from "./compileTemplateSelector";
import {
    cacheGet,
    cacheSet,
    clearCacheByPrefix,
    getCacheStats,
    CACHE_PREFIX_TEMPLATE_DEXIE,
    scheduleTemplatePersist,
    getPersistedTemplates,
    setWarmingFlag,
} from "./queryCache";

// ============================================================================
// Types
// ============================================================================

/**
 * Pushdown strategy with value indices for parameterized execution.
 * Values are stored as indices into the values array.
 */
type TemplatePushdown =
    | { kind: "anyOf"; field: string; valuesIdx: number }
    | { kind: "eq" | "gt" | "lt" | "gte" | "lte" | "ne"; field: string; valueIdx: number }
    | { kind: "startsWith"; field: string; prefixIdx: number }
    | {
          kind: "between";
          field: string;
          lowerIdx: number;
          upperIdx: number;
          includeLower: boolean;
          includeUpper: boolean;
      }
    | { kind: "multiEq"; fieldIndices: Record<string, number> };

/** Cached result of template query analysis */
interface AnalyzedTemplate {
    push: TemplatePushdown | undefined;
    residualTemplate: MangoSelector;
    residualPredicate: ParameterizedPredicate | null;
    /** Top-level equality fields → value index (empty when a field has conflicting values). */
    eqFields: Record<string, number>;
    /** Top-level `$in` fields → value index. */
    inFields: Record<string, number>;
    /** The whole template compiled, built on first use by a key lookup plan. */
    fullPredicate?: ParameterizedPredicate;
}

/**
 * An exact key lookup chosen at run time from the table schema: direct primary-key reads, or
 * `anyOf` on a compound index covering every equality field plus one `$in` field.
 */
type CompoundKeyLookup = { kind: "compound"; index: string; keys: unknown[][] };
type KeyLookupPlan = { kind: "primaryKey"; keys: unknown[] } | CompoundKeyLookup;
type EqualityLookup = { kind: "equality"; index: string; key: unknown };
type IndexLookup = CompoundKeyLookup | EqualityLookup;

// ============================================================================
// Module-level Constants
// ============================================================================

const COMBINATION_OPERATORS = new Set(["$and", "$or", "$not", "$nor"]);

/** A sorted, limited query reads an index lookup directly when it holds at most this many rows per result. */
const LOOKUP_ROWS_PER_LIMIT = 4;

// ============================================================================
// Cache Management Exports
// ============================================================================

/**
 * Clear all cached Dexie query analysis results.
 * Does not affect mangoCompile cache.
 */
export function clearDexieCache(): void {
    clearCacheByPrefix(CACHE_PREFIX_TEMPLATE_DEXIE);
}

/**
 * Get cache statistics for mangoToDexie.
 */
export function getDexieCacheStats(): { size: number; keys: string[] } {
    return getCacheStats(CACHE_PREFIX_TEMPLATE_DEXIE);
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Convert a Mango query into a Dexie result array, pushing index-friendly parts into Dexie.
 *
 * Returns `Promise<T[]>` directly, handling sorting and limiting internally for
 * optimal performance. When sorting without a limit, the function uses index-based
 * filtering first (via pushdown), then sorts the smaller result set in memory using
 * `Collection.sortBy()`. When sorting with a limit, it uses `table.orderBy()` for
 * early termination.
 *
 * Uses template-based caching: the query structure is normalized, and the analysis
 * (pushdown strategy and residual selector) is cached based on the structure alone.
 * This allows queries with the same structure but different values to share the
 * same analysis, with values applied at runtime.
 *
 * Supported pushdown operators:
 * - `$eq` / primitive equality → `where(field).equals(value)` or `where({ f1: v1, ... })`
 * - `$ne` → `where(field).notEqual(value)`
 * - `$gt` / `$lt` / `$gte` / `$lte` → `above` / `below` / `aboveOrEqual` / `belowOrEqual`
 * - Combined `$gte` + `$lte` on same field → `where(field).between(lower, upper)`
 * - `$in` → `where(field).anyOf(values)`, or `table.bulkGet(values)` when targeting primary key
 * - `$beginsWith` → `where(field).startsWith(prefix)`
 *
 * All other operators are handled in-memory via the residual filter.
 */
export function mangoToDexie<T = any>(table: Table, query: MangoQuery): Promise<T[]> {
    const selector: MangoSelector = (query?.selector || {}) as MangoSelector;

    // Unsatisfiable by construction (e.g. an empty `$in`) → skip IndexedDB entirely
    // instead of scanning + filtering every row out. Sound; see isProvablyEmpty.
    if (isProvablyEmpty(selector)) return Promise.resolve([] as T[]);

    const limit = typeof query?.$limit === "number" ? query.$limit : undefined;
    const sort = Array.isArray(query?.$sort) ? query.$sort : undefined;

    // Normalize selector and analyze template (cached)
    const { template, values } = normalizeSelector(selector);
    const analysis = analyzeTemplate(template);

    // Fast path: bulkGet when $in targets the primary key.
    // Uses direct key lookups instead of cursor-based anyOf scan.
    if (analysis.push?.kind === "anyOf" && isPrimaryKeyField(table, analysis.push.field)) {
        const keys = values[analysis.push.valuesIdx] as unknown[];
        return executeBulkGet<T>(table, keys, analysis.residualPredicate, values, sort, limit);
    }

    // Equality fields win the template pushdown, so an id list next to them would otherwise
    // scan the equality range and filter it. Look the ids up directly when the schema allows.
    const keyPlan = planKeyLookup(table, analysis, values);
    if (keyPlan?.kind === "primaryKey" && sort?.length && typeof limit === "number") {
        // This query shape used to walk the sort index, so its rows keep that order.
        const entry = sort[0];
        const sortField = Object.keys(entry)[0];
        const desc = (entry as Record<string, string>)[sortField] === "desc";
        const predicate = fullPredicate(analysis, template);
        return table.bulkGet(keyPlan.keys as any).then((docs) => {
            const matches = docs.filter(
                (doc) => doc !== undefined && predicate(doc, values),
            ) as T[];
            return sortLikeIndex(table, matches, sortField, desc).slice(0, Math.max(0, limit));
        }) as Promise<T[]>;
    }
    if (keyPlan?.kind === "primaryKey") {
        return executeBulkGet<T>(
            table,
            keyPlan.keys,
            fullPredicate(analysis, template),
            values,
            sort,
            limit,
        );
    }
    const keyedCollection = (plan: IndexLookup) => {
        const predicate = fullPredicate(analysis, template);
        return lookupRange(table, plan).filter((doc) => predicate(doc, values));
    };

    // Apply sorting and limiting
    if (sort && sort.length > 0) {
        const entry = sort[0];
        const sortField = Object.keys(entry)[0];
        const desc = (entry as Record<string, string>)[sortField] === "desc";

        if (typeof limit === "number") {
            // Sort + limit: use orderBy for early termination. This scans rows in
            // index order and stops after enough matches, so it does NOT use the
            // index-pushdown collection (building it would only waste work and emit a
            // spurious "missing index" warning for a query that is index-ordered).
            const walkSortIndex = () => {
                let ordered: Collection;
                try {
                    ordered = table.orderBy(sortField);
                    if (desc) ordered = ordered.reverse();
                } catch {
                    ordered = table.filter(() => true);
                }

                const pred = mangoCompile(selector) as (d: unknown) => boolean;
                ordered = ordered.filter(pred);
                return ordered.limit(Math.max(0, limit)).toArray() as Promise<T[]>;
            };
            const lookup = keyPlan ?? planEqualityLookup(table, analysis, values);
            if (!lookup) return walkSortIndex();

            // The walk stops after `limit` matches but reads the whole table when matches are
            // rare. It always reads at least `limit` rows, so a lookup of up to a few times that
            // is the cheaper plan; a larger range is abandoned after reading only its keys.
            const bound = Math.max(1, limit) * LOOKUP_ROWS_PER_LIMIT;
            return lookupRange(table, lookup)
                .limit(bound + 1)
                .primaryKeys()
                .then((keys) => {
                    if (keys.length > bound) return walkSortIndex();
                    const predicate = fullPredicate(analysis, template);
                    return table.bulkGet(keys).then((docs) => {
                        const matches = docs.filter(
                            (doc) => doc !== undefined && predicate(doc, values),
                        ) as T[];
                        return sortLikeIndex(table, matches, sortField, desc).slice(
                            0,
                            Math.max(0, limit),
                        );
                    });
                }) as Promise<T[]>;
        }

        // Sort + no limit: filter first using index pushdown, then sort in memory.
        // This is much faster than scanning the entire table via orderBy.
        const col = keyPlan
            ? keyedCollection(keyPlan)
            : buildFilteredCollection(table, selector, analysis, values);
        const result = col.sortBy(sortField) as Promise<T[]>;
        return desc ? result.then((arr) => arr.reverse()) : result;
    }

    // No sorting: build the index-pushdown collection and apply limit if specified.
    const col = keyPlan
        ? keyedCollection(keyPlan)
        : buildFilteredCollection(table, selector, analysis, values);
    if (typeof limit === "number") {
        return col.limit(Math.max(0, limit)).toArray() as Promise<T[]>;
    }
    return col.toArray() as Promise<T[]>;
}

/** The whole template as one predicate, compiled once per cached analysis. */
function fullPredicate(
    analysis: AnalyzedTemplate,
    template: MangoSelector,
): ParameterizedPredicate {
    if (!analysis.fullPredicate) analysis.fullPredicate = compileTemplateSelector(template);
    return analysis.fullPredicate;
}

const isIndexableKey = (value: unknown): boolean =>
    typeof value === "string" ||
    (typeof value === "number" && !Number.isNaN(value)) ||
    value instanceof Date;

/**
 * Choose an exact key lookup for a top-level `$in`: the primary key, or a compound index whose
 * fields are exactly the equality fields plus the `$in` field. A partial cover is not chosen,
 * because the equality-only index it would replace can be the more selective one.
 */
function planKeyLookup(
    table: Table,
    analysis: AnalyzedTemplate,
    values: unknown[],
): KeyLookupPlan | undefined {
    const inFields = Object.keys(analysis.inFields);
    if (inFields.length === 0) return undefined;

    let schema: Table["schema"];
    try {
        schema = table.schema;
    } catch {
        return undefined;
    }
    if (!schema) return undefined;

    const pk = schema.primKey?.keyPath;
    if (typeof pk === "string" && analysis.inFields[pk] !== undefined) {
        const keys = values[analysis.inFields[pk]];
        return Array.isArray(keys) ? { kind: "primaryKey", keys } : undefined;
    }

    const eqFields = Object.keys(analysis.eqFields);
    if (eqFields.length === 0) return undefined;

    for (const inField of inFields) {
        if (analysis.eqFields[inField] !== undefined) continue;
        const wanted = new Set([...eqFields, inField]);
        const index = schema.indexes.find(
            (idx) =>
                Array.isArray(idx.keyPath) &&
                !idx.multi &&
                idx.keyPath.length === wanted.size &&
                idx.keyPath.every((field) => wanted.has(field)),
        );
        if (!index) continue;

        const inValues = values[analysis.inFields[inField]];
        if (!Array.isArray(inValues) || !inValues.every(isIndexableKey)) continue;
        const keyPath = index.keyPath as string[];
        const fixed = keyPath.map((field) =>
            field === inField ? undefined : values[analysis.eqFields[field]],
        );
        if (!fixed.every((value, i) => keyPath[i] === inField || isIndexableKey(value))) continue;

        const keys = Array.from(new Set(inValues)).map((value) =>
            keyPath.map((field, i) => (field === inField ? value : fixed[i])),
        );
        return { kind: "compound", index: indexName(keyPath), keys };
    }
    return undefined;
}

/**
 * An index that holds exactly the equality fields, looked up by their values: the template
 * pushdown's own range, sized and read directly when a sorted query has a limit.
 */
function planEqualityLookup(
    table: Table,
    analysis: AnalyzedTemplate,
    values: unknown[],
): EqualityLookup | undefined {
    if (analysis.push?.kind !== "multiEq") return undefined;
    const fields = Object.keys(analysis.push.fieldIndices);
    const fieldIndices = analysis.push.fieldIndices;

    let schema: Table["schema"];
    try {
        schema = table.schema;
    } catch {
        return undefined;
    }
    const index = schema?.indexes.find((idx) =>
        fields.length === 1
            ? idx.keyPath === fields[0] && !idx.multi
            : Array.isArray(idx.keyPath) &&
              !idx.multi &&
              idx.keyPath.length === fields.length &&
              idx.keyPath.every((field) => fieldIndices[field] !== undefined),
    );
    if (!index) return undefined;

    const keyPath = Array.isArray(index.keyPath) ? index.keyPath : [index.keyPath as string];
    const key = keyPath.map((field) => values[fieldIndices[field]]);
    if (!key.every(isIndexableKey)) return undefined;
    return {
        kind: "equality",
        index: indexName(keyPath),
        key: Array.isArray(index.keyPath) ? key : key[0],
    };
}

/** Dexie's name for an index: the field, or `[a+b]` for a compound key path. */
const indexName = (keyPath: string[]): string =>
    keyPath.length === 1 ? keyPath[0] : `[${keyPath.join("+")}]`;

/** Rows whose index key matches the plan, before any filtering. */
function lookupRange(table: Table, plan: IndexLookup): Collection {
    const clause = (table.where as (index: string) => DexieWhereClause)(plan.index);
    return plan.kind === "compound" ? clause.anyOf(plan.keys) : clause.equals(plan.key);
}

/**
 * Sort rows the way a cursor over the sort field's index returns them: rows without a value
 * are left out, and equal values keep primary-key order (reversed for descending).
 */
function sortLikeIndex<T>(table: Table, docs: T[], sortField: string, desc: boolean): T[] {
    const pk = table.schema?.primKey?.keyPath;
    const pkField = typeof pk === "string" ? pk : undefined;
    const get = (doc: T, field: string) => (doc as Record<string, unknown>)[field] as any;
    const sorted = docs
        .filter((doc) => isIndexableKey(get(doc, sortField)))
        .sort((a, b) => {
            const av = get(a, sortField);
            const bv = get(b, sortField);
            if (av < bv) return -1;
            if (av > bv) return 1;
            if (!pkField) return 0;
            const ak = get(a, pkField);
            const bk = get(b, pkField);
            return ak < bk ? -1 : ak > bk ? 1 : 0;
        });
    return desc ? sorted.reverse() : sorted;
}

/**
 * Build a filtered Dexie Collection using template-based index pushdown.
 * This is the core filtering logic separated from sorting/limiting concerns.
 */
function buildFilteredCollection(
    table: Table,
    selector: MangoSelector,
    analysis: AnalyzedTemplate,
    values: unknown[],
): Collection {
    if (analysis.push && isPushdownValid(table, analysis.push, selector)) {
        // Apply the pushdown with actual values
        let col = applyTemplateWhere(table, analysis.push, values);

        // Apply residual filter if needed
        if (analysis.residualPredicate) {
            col = col.filter((doc) => analysis.residualPredicate!(doc, values));
        }

        return col;
    }

    // Fallback: in-memory filter for full selector
    const pred = mangoCompile(selector) as (d: unknown) => boolean;
    return table.filter(pred);
}

// ============================================================================
// Primary Key BulkGet Optimization
// ============================================================================

/**
 * Check if a field matches the table's primary key path.
 * Returns false for compound keys, out-of-line keys, or if schema is unavailable.
 */
function isPrimaryKeyField(table: Table, field: string): boolean {
    try {
        const keyPath = table.schema?.primKey?.keyPath;
        return typeof keyPath === "string" && keyPath === field;
    } catch {
        return false;
    }
}

/**
 * Execute a bulkGet for $in queries on the primary key.
 * This is faster than where(pk).anyOf(values) because it uses direct
 * key lookups (IDBObjectStore.get) instead of cursor-based index scans.
 * Sort and limit are applied in-memory on the (typically small) result set.
 */
async function executeBulkGet<T>(
    table: Table,
    keys: unknown[],
    predicate: ParameterizedPredicate | null,
    values: unknown[],
    sort: Record<string, string>[] | undefined,
    limit: number | undefined,
): Promise<T[]> {
    // Direct primary key lookup
    const raw = await table.bulkGet(keys as any);

    // Filter out undefined (missing keys) and apply residual predicate
    let results: T[];
    if (predicate) {
        results = [];
        for (let i = 0; i < raw.length; i++) {
            const doc = raw[i];
            if (doc !== undefined && predicate(doc, values)) {
                results.push(doc as T);
            }
        }
    } else {
        results = [];
        for (let i = 0; i < raw.length; i++) {
            if (raw[i] !== undefined) {
                results.push(raw[i] as T);
            }
        }
    }

    // Apply sort if specified
    if (sort && sort.length > 0) {
        const entry = sort[0];
        // Use for...in instead of Object.keys() to avoid temporary array allocation
        let sortField = "";
        for (const k in entry) {
            sortField = k;
            break;
        }
        const desc = (entry as Record<string, string>)[sortField] === "desc";

        results.sort((a, b) => {
            const av = (a as Record<string, unknown>)[sortField];
            const bv = (b as Record<string, unknown>)[sortField];
            if (av == null && bv == null) return 0;
            if (av == null) return -1;
            if (bv == null) return 1;
            if (av < bv) return -1;
            if (av > bv) return 1;
            return 0;
        });

        if (desc) results.reverse();
    }

    // Apply limit
    if (typeof limit === "number") {
        results = results.slice(0, Math.max(0, limit));
    }

    return results;
}

// ============================================================================
// Template Analysis (Cached)
// ============================================================================

/**
 * Analyze a template selector to extract pushdown strategy and residual.
 * Results are cached for repeated queries with the same structure.
 */
function analyzeTemplate(template: MangoSelector): AnalyzedTemplate {
    const cacheKey = generateTemplateKey(template, CACHE_PREFIX_TEMPLATE_DEXIE);

    const cached = cacheGet<AnalyzedTemplate>(cacheKey);
    if (cached) {
        return cached;
    }

    // Expand the template to $and form for analysis
    const expanded = expandMangoSelector(template);
    const andConditions = expanded.$and || [];

    // Extract pushdown strategy from template
    const { push, eqFields, inFields } = extractTemplatePushdown(andConditions);

    // Build residual template (parts not handled by pushdown)
    let residualTemplate: MangoSelector;
    let residualPredicate: ParameterizedPredicate | null = null;

    if (push) {
        residualTemplate = buildResidualTemplate(andConditions, push);
        if (!isEmptySelector(residualTemplate)) {
            residualPredicate = compileTemplateSelector(residualTemplate);
        }
    } else {
        residualTemplate = template;
        residualPredicate = compileTemplateSelector(template);
    }

    const result: AnalyzedTemplate = {
        push,
        residualTemplate,
        residualPredicate,
        eqFields,
        inFields,
    };

    cacheSet(cacheKey, result);
    scheduleTemplatePersist(cacheKey, template);
    return result;
}

/**
 * Pre-warm the mangoToDexie analysis cache from templates persisted in localStorage.
 *
 * Call this early in app startup (before components mount) to eliminate
 * cold-start query analysis latency. Templates are automatically persisted
 * as queries execute; on subsequent page loads this function restores and
 * re-analyzes them so the first real query hits a warm cache.
 */
export function warmDexieAnalysisCache(): void {
    setWarmingFlag(true);
    try {
        const persisted = getPersistedTemplates(CACHE_PREFIX_TEMPLATE_DEXIE);
        persisted.forEach((template) => {
            // analyzeTemplate handles its own cache-key generation and caching
            analyzeTemplate(template as MangoSelector);
        });
    } finally {
        setWarmingFlag(false);
    }
}

// ============================================================================
// Template Pushdown Extraction
// ============================================================================

function isEmptySelector(q: MangoSelector): boolean {
    let keyCount = 0;
    let firstKey: string | null = null;

    for (const k in q) {
        if (keyCount === 0) firstKey = k;
        keyCount++;
        if (keyCount > 1) break;
    }

    if (keyCount === 0) return true;
    if (keyCount === 1 && firstKey === "$and") {
        const andArr = q.$and;
        return !andArr || andArr.length === 0;
    }
    return false;
}

/** Collected pushdown data from template analysis */
interface CollectedTemplatePushdownData {
    /** Equality fields with their value indices */
    eqMap: Record<string, number>;
    /** First $beginsWith found */
    startsWith: { field: string; prefixIdx: number } | null;
    /** Range bounds by field */
    rangeMap: Record<string, { gteIdx?: number; lteIdx?: number; gtIdx?: number; ltIdx?: number }>;
    /** First $in found */
    anyOf: { field: string; valuesIdx: number } | null;
    /** Every $in field with its values index (first occurrence per field) */
    inMap: Record<string, number>;
    /** First single comparator found */
    singleComparator: TemplatePushdown | null;
}

/**
 * Extract pushdown strategy from template conditions.
 * Uses placeholder indices instead of actual values.
 * Note: Static boolean values in templates are skipped for pushdown
 * (Dexie's where() doesn't work well with boolean indexes).
 */
function extractTemplatePushdown(conditions: MangoSelector[]): {
    push: TemplatePushdown | undefined;
    eqFields: Record<string, number>;
    inFields: Record<string, number>;
} {
    const data: CollectedTemplatePushdownData = {
        eqMap: {},
        startsWith: null,
        rangeMap: {},
        anyOf: null,
        inMap: {},
        singleComparator: null,
    };

    let eqConflict = false;

    for (let i = 0; i < conditions.length; i++) {
        const cond = conditions[i];

        let field: string | null = null;
        let keyCount = 0;
        for (const k in cond) {
            if (keyCount === 0) field = k;
            keyCount++;
            if (keyCount > 1) break;
        }

        if (keyCount !== 1 || field === null) continue;
        if (COMBINATION_OPERATORS.has(field)) continue;

        const criteria = (cond as Record<string, unknown>)[field];

        // Skip static boolean values - they're kept in template but not pushed to Dexie
        if (typeof criteria === "boolean") {
            continue;
        }

        // Check for placeholder (direct field equality)
        if (isPlaceholder(criteria)) {
            const idx = criteria.$__idx;
            if (!eqConflict) {
                if (data.eqMap[field] !== undefined && data.eqMap[field] !== idx) {
                    eqConflict = true;
                    data.eqMap = {};
                } else {
                    data.eqMap[field] = idx;
                }
            }
            if (!data.singleComparator) {
                data.singleComparator = { kind: "eq", field, valueIdx: idx };
            }
            continue;
        }

        // Object criteria with operators
        if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
            const critObj = criteria as Record<string, unknown>;

            // Check $eq
            const eqVal = critObj.$eq;
            if (isPlaceholder(eqVal)) {
                const idx = eqVal.$__idx;
                if (!eqConflict) {
                    if (data.eqMap[field] !== undefined && data.eqMap[field] !== idx) {
                        eqConflict = true;
                        data.eqMap = {};
                    } else {
                        data.eqMap[field] = idx;
                    }
                }
                if (!data.singleComparator) {
                    data.singleComparator = { kind: "eq", field, valueIdx: idx };
                }
            }

            // Check $beginsWith
            const prefix = critObj.$beginsWith;
            if (!data.startsWith && isPlaceholder(prefix)) {
                data.startsWith = { field, prefixIdx: prefix.$__idx };
            }

            // Check range operators
            const gteVal = critObj.$gte;
            const lteVal = critObj.$lte;
            const gtVal = critObj.$gt;
            const ltVal = critObj.$lt;

            if (
                isPlaceholder(gteVal) ||
                isPlaceholder(lteVal) ||
                isPlaceholder(gtVal) ||
                isPlaceholder(ltVal)
            ) {
                if (!data.rangeMap[field]) data.rangeMap[field] = {};
                if (isPlaceholder(gteVal)) data.rangeMap[field].gteIdx = gteVal.$__idx;
                if (isPlaceholder(lteVal)) data.rangeMap[field].lteIdx = lteVal.$__idx;
                if (isPlaceholder(gtVal)) data.rangeMap[field].gtIdx = gtVal.$__idx;
                if (isPlaceholder(ltVal)) data.rangeMap[field].ltIdx = ltVal.$__idx;
            }

            // Check $in
            const inVal = critObj.$in;
            if (isPlaceholder(inVal)) {
                if (!data.anyOf) data.anyOf = { field, valuesIdx: inVal.$__idx };
                if (data.inMap[field] === undefined) data.inMap[field] = inVal.$__idx;
            }

            // Check single comparators
            if (!data.singleComparator) {
                if (isPlaceholder(gtVal)) {
                    data.singleComparator = { kind: "gt", field, valueIdx: gtVal.$__idx };
                } else if (isPlaceholder(ltVal)) {
                    data.singleComparator = { kind: "lt", field, valueIdx: ltVal.$__idx };
                } else if (isPlaceholder(gteVal)) {
                    data.singleComparator = { kind: "gte", field, valueIdx: gteVal.$__idx };
                } else if (isPlaceholder(lteVal)) {
                    data.singleComparator = { kind: "lte", field, valueIdx: lteVal.$__idx };
                } else if (isPlaceholder(critObj.$ne)) {
                    data.singleComparator = {
                        kind: "ne",
                        field,
                        valueIdx: (critObj.$ne as { $__idx: number }).$__idx,
                    };
                }
            }
        }
    }

    return { push: choosePushdown(data), eqFields: data.eqMap, inFields: data.inMap };
}

/** Pick one pushdown strategy from the collected conditions, by priority. */
function choosePushdown(data: CollectedTemplatePushdownData): TemplatePushdown | undefined {
    // Priority 1: multiEq (compound index) - only when 2+ eq fields.
    // A single eq field should not take priority over anyOf, which is typically
    // more selective (e.g. $in on a foreign key vs eq on a status field).
    let eqFieldCount = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const _key in data.eqMap) {
        eqFieldCount++;
        if (eqFieldCount > 1) break;
    }
    if (eqFieldCount >= 2 || (eqFieldCount === 1 && !data.anyOf)) {
        return { kind: "multiEq", fieldIndices: data.eqMap };
    }

    // Priority 2: startsWith
    if (data.startsWith) {
        return {
            kind: "startsWith",
            field: data.startsWith.field,
            prefixIdx: data.startsWith.prefixIdx,
        };
    }

    // Priority 3: between
    for (const field in data.rangeMap) {
        const range = data.rangeMap[field];
        const hasLower = range.gteIdx !== undefined || range.gtIdx !== undefined;
        const hasUpper = range.lteIdx !== undefined || range.ltIdx !== undefined;

        if (hasLower && hasUpper) {
            const lowerIdx = range.gteIdx !== undefined ? range.gteIdx : range.gtIdx!;
            const upperIdx = range.lteIdx !== undefined ? range.lteIdx : range.ltIdx!;
            const includeLower = range.gteIdx !== undefined;
            const includeUpper = range.lteIdx !== undefined;
            return { kind: "between", field, lowerIdx, upperIdx, includeLower, includeUpper };
        }
    }

    // Priority 4: anyOf
    if (data.anyOf) {
        return { kind: "anyOf", field: data.anyOf.field, valuesIdx: data.anyOf.valuesIdx };
    }

    // Priority 5: single comparator
    return data.singleComparator || undefined;
}

// ============================================================================
// Pushdown Validation
// ============================================================================

/**
 * Extract top-level field names from a Mango selector, flattening $and arrays.
 * Used to suggest compound indexes covering all queried fields.
 */
function extractQueriedFields(selector: MangoSelector): string[] {
    const fields: string[] = [];
    const seen = new Set<string>();

    const collect = (conditions: MangoSelector[]) => {
        for (const cond of conditions) {
            for (const key in cond) {
                if (key === "$and") {
                    collect(cond.$and!);
                } else if (!COMBINATION_OPERATORS.has(key) && !seen.has(key)) {
                    seen.add(key);
                    fields.push(key);
                }
            }
        }
    };

    collect([selector]);
    return fields;
}

/**
 * Check whether all fields referenced by a pushdown strategy are actually
 * indexed on the target table. Returns false if any field is missing from
 * the schema, which would cause Dexie to throw a SchemaError at query time.
 */
function isPushdownValid(table: Table, push: TemplatePushdown, selector: MangoSelector): boolean {
    try {
        const schema = table.schema;

        // Collect single-field indexes
        const singleIndexed = new Set<string>();
        const pk = schema.primKey?.keyPath;
        if (typeof pk === "string") singleIndexed.add(pk);

        for (const idx of schema.indexes) {
            if (typeof idx.keyPath === "string") {
                singleIndexed.add(idx.keyPath);
            }
        }

        // Collect compound indexes as sorted field sets for matching
        const compoundIndexes: string[][] = [];
        for (const idx of schema.indexes) {
            if (Array.isArray(idx.keyPath)) {
                compoundIndexes.push(idx.keyPath.slice().sort());
            }
        }

        // Extract top-level queried fields from the selector for compound index suggestions
        const queriedFields = extractQueriedFields(selector);
        const compoundHint =
            queriedFields.length > 1 ? `[${queriedFields.join("+")}]` : queriedFields[0] || "";

        const warn = () => {
            console.warn(
                `[mangoToDexie] Missing index on table "${table.name}". ` +
                    `Falling back to full table scan. Consider adding index: ${compoundHint}`,
            );
        };

        if (push.kind === "multiEq") {
            const fields = Object.keys(push.fieldIndices);

            // Single field multiEq can use a single-field index
            if (fields.length === 1) {
                if (singleIndexed.has(fields[0])) return true;
                warn();
                return false;
            }

            // Check for a compound index covering exactly these fields (most efficient)
            const sortedFields = fields.slice().sort();
            const hasCompound = compoundIndexes.some(
                (idx) =>
                    idx.length === sortedFields.length &&
                    idx.every((field, i) => field === sortedFields[i]),
            );

            if (hasCompound) return true;

            // Dexie's where({...}) also works with single-field indexes — it picks the
            // best index for one field and filters the rest in memory. Allow multiEq
            // if at least one field has a single-field index.
            const hasAnySingleIndex = fields.some((f) => singleIndexed.has(f));
            if (hasAnySingleIndex) {
                warn();
                return true;
            }

            warn();
            return false;
        }

        if (!singleIndexed.has(push.field)) {
            warn();
            return false;
        }

        return true;
    } catch (error) {
        console.warn(
            `[mangoToDexie] Missing index on table "${table.name}". ` +
                `Falling back to full table scan.`,
            error,
        );
        return false;
    }
}

// ============================================================================
// Where Application with Values
// ============================================================================

function applyTemplateWhere(table: Table, push: TemplatePushdown, values: unknown[]): Collection {
    if (push.kind === "multiEq") {
        // Build the where object from field indices and actual values
        const whereObj: Record<string, unknown> = {};
        for (const field in push.fieldIndices) {
            whereObj[field] = values[push.fieldIndices[field]];
        }
        return (table.where as (arg: Record<string, unknown>) => Collection)(whereObj);
    }

    const clause = (table.where as (field: string) => DexieWhereClause)(push.field);

    switch (push.kind) {
        case "anyOf":
            return clause.anyOf(values[push.valuesIdx] as unknown[]);
        case "eq":
            return clause.equals(values[push.valueIdx]);
        case "gt":
            return clause.above(values[push.valueIdx]);
        case "lt":
            return clause.below(values[push.valueIdx]);
        case "gte":
            return clause.aboveOrEqual(values[push.valueIdx]);
        case "lte":
            return clause.belowOrEqual(values[push.valueIdx]);
        case "ne":
            return clause.notEqual(values[push.valueIdx]);
        case "startsWith":
            return clause.startsWith(values[push.prefixIdx] as string);
        case "between":
            return clause.between(
                values[push.lowerIdx],
                values[push.upperIdx],
                push.includeLower,
                push.includeUpper,
            );
    }
}

interface DexieWhereClause {
    anyOf(values: unknown[]): Collection;
    equals(value: unknown): Collection;
    above(value: unknown): Collection;
    below(value: unknown): Collection;
    aboveOrEqual(value: unknown): Collection;
    belowOrEqual(value: unknown): Collection;
    notEqual(value: unknown): Collection;
    startsWith(prefix: string): Collection;
    between(
        lower: unknown,
        upper: unknown,
        includeLower?: boolean,
        includeUpper?: boolean,
    ): Collection;
}

// ============================================================================
// Residual Template Building
// ============================================================================

/**
 * Build residual template from conditions, removing the pushed parts.
 */
function buildResidualTemplate(conditions: MangoSelector[], push: TemplatePushdown): MangoSelector {
    const residualConditions: MangoSelector[] = [];

    for (let i = 0; i < conditions.length; i++) {
        const cleaned = cleanTemplateCondition(conditions[i], push);
        if (cleaned !== null) {
            // Check for non-empty without Object.keys() allocation
            let hasKeys = false;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            for (const _key in cleaned) {
                hasKeys = true;
                break;
            }
            if (hasKeys) {
                residualConditions.push(cleaned);
            }
        }
    }

    if (residualConditions.length === 0) {
        return {};
    }

    if (residualConditions.length === 1) {
        return residualConditions[0];
    }

    return { $and: residualConditions };
}

/**
 * Clean a single condition by removing pushed parts.
 */
function cleanTemplateCondition(cond: MangoSelector, push: TemplatePushdown): MangoSelector | null {
    let field: string | null = null;
    let keyCount = 0;
    for (const k in cond) {
        if (keyCount === 0) field = k;
        keyCount++;
        if (keyCount > 1) break;
    }

    if (keyCount !== 1 || field === null) return cond;
    if (COMBINATION_OPERATORS.has(field)) return cond;

    const criteria = (cond as Record<string, unknown>)[field];

    // Handle multiEq pushdown
    if (push.kind === "multiEq") {
        if (!(field in push.fieldIndices)) return cond;
        const expectedIdx = push.fieldIndices[field];

        // Direct placeholder
        if (isPlaceholder(criteria) && criteria.$__idx === expectedIdx) {
            return null;
        }

        // $eq operator
        if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
            const critObj = criteria as Record<string, unknown>;
            const eqVal = critObj.$eq;
            if (isPlaceholder(eqVal) && eqVal.$__idx === expectedIdx) {
                // Check if $eq is the only key
                let otherKeys = false;
                for (const k in critObj) {
                    if (k !== "$eq") {
                        otherKeys = true;
                        break;
                    }
                }
                if (!otherKeys) return null;
                // Copy without $eq
                const copy: Record<string, unknown> = {};
                for (const k in critObj) {
                    if (k !== "$eq") copy[k] = critObj[k];
                }
                return { [field]: copy };
            }
        }

        return cond;
    }

    // For single-field pushdowns, only clean if field matches
    if (push.field !== field) {
        return cond;
    }

    switch (push.kind) {
        case "eq": {
            if (isPlaceholder(criteria) && criteria.$__idx === push.valueIdx) {
                return null;
            }
            if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
                const critObj = criteria as Record<string, unknown>;
                const eqVal = critObj.$eq;
                if (isPlaceholder(eqVal) && eqVal.$__idx === push.valueIdx) {
                    return removeOperatorFromCriteria(field, critObj, "$eq");
                }
            }
            return cond;
        }

        case "ne":
        case "gt":
        case "lt":
        case "gte":
        case "lte": {
            if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
                const critObj = criteria as Record<string, unknown>;
                const opKey =
                    push.kind === "ne"
                        ? "$ne"
                        : push.kind === "gt"
                          ? "$gt"
                          : push.kind === "lt"
                            ? "$lt"
                            : push.kind === "gte"
                              ? "$gte"
                              : "$lte";
                const opVal = critObj[opKey];
                if (isPlaceholder(opVal) && opVal.$__idx === push.valueIdx) {
                    return removeOperatorFromCriteria(field, critObj, opKey);
                }
            }
            return cond;
        }

        case "anyOf": {
            if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
                const critObj = criteria as Record<string, unknown>;
                const inVal = critObj.$in;
                if (isPlaceholder(inVal) && inVal.$__idx === push.valuesIdx) {
                    return removeOperatorFromCriteria(field, critObj, "$in");
                }
            }
            return cond;
        }

        case "startsWith": {
            if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
                const critObj = criteria as Record<string, unknown>;
                const bwVal = critObj.$beginsWith;
                if (isPlaceholder(bwVal) && bwVal.$__idx === push.prefixIdx) {
                    return removeOperatorFromCriteria(field, critObj, "$beginsWith");
                }
            }
            return cond;
        }

        case "between": {
            if (criteria && typeof criteria === "object" && !Array.isArray(criteria)) {
                const critObj = criteria as Record<string, unknown>;
                const copy: Record<string, unknown> = {};
                let hasRemaining = false;

                for (const k in critObj) {
                    let shouldRemove = false;

                    if (push.includeLower && k === "$gte") {
                        const v = critObj[k];
                        if (isPlaceholder(v) && v.$__idx === push.lowerIdx) shouldRemove = true;
                    }
                    if (!push.includeLower && k === "$gt") {
                        const v = critObj[k];
                        if (isPlaceholder(v) && v.$__idx === push.lowerIdx) shouldRemove = true;
                    }
                    if (push.includeUpper && k === "$lte") {
                        const v = critObj[k];
                        if (isPlaceholder(v) && v.$__idx === push.upperIdx) shouldRemove = true;
                    }
                    if (!push.includeUpper && k === "$lt") {
                        const v = critObj[k];
                        if (isPlaceholder(v) && v.$__idx === push.upperIdx) shouldRemove = true;
                    }

                    if (!shouldRemove) {
                        copy[k] = critObj[k];
                        hasRemaining = true;
                    }
                }

                return hasRemaining ? { [field]: copy } : null;
            }
            return cond;
        }

        default:
            return cond;
    }
}

/**
 * Helper to remove a single operator from criteria object.
 */
function removeOperatorFromCriteria(
    field: string,
    critObj: Record<string, unknown>,
    opKey: string,
): MangoSelector | null {
    let hasOther = false;
    for (const k in critObj) {
        if (k !== opKey) {
            hasOther = true;
            break;
        }
    }

    if (!hasOther) return null;

    const copy: Record<string, unknown> = {};
    for (const k in critObj) {
        if (k !== opKey) copy[k] = critObj[k];
    }
    return { [field]: copy };
}
